import { useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Bot,
    ChevronRight,
    CircleCheckBig,
    EllipsisVertical,
    FileIcon,
    TriangleAlert,
    Zap,
} from "lucide-react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { store } from "../../store";
import { toast } from "sonner";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import {
    type RobotAvailability,
    type ChargingState,
    type ConnectionStatus,
    type RobotHealth,
    type Robot,
    type RobotType,
    type LifecycleState,
    type RobotSortKey,
} from "../types";
import { SortDirection } from "../../api/models";
import { DecoratedIcon } from "@/components/ui/decorated-icon";

// The labels in the following configs are translation keys.

const connectivityConfig: Record<ConnectionStatus, { label: string; dotClassName: string }> = {
    online: { label: "page.robots.connectivity.online", dotClassName: "bg-green-500" },
    delayed: { label: "page.robots.connectivity.delayed", dotClassName: "bg-amber-500" },
    offline: { label: "page.robots.connectivity.offline", dotClassName: "bg-zinc-400" },
};

const typeLabels: Record<RobotType, string> = {
    unit_load: "page.robots.type.unitLoad",
    forklift: "page.robots.type.forklift",
    tugger: "page.robots.type.tugger",
    cart: "page.robots.type.cart",
    cleaner: "page.robots.type.cleaner",
    pallet_mover: "page.robots.type.palletMover",
};

const lifecycleConfig: Record<LifecycleState, { label: string; className: string }> = {
    planned: { label: "page.robots.lifecycle.planned", className: "text-blue-600" },
    commissioning: { label: "page.robots.lifecycle.commissioning", className: "text-blue-600" },
    active: { label: "page.robots.lifecycle.active", className: "text-blue-600" },
    suspended: { label: "page.robots.lifecycle.suspended", className: "text-blue-600" },
    maintenance: { label: "page.robots.lifecycle.maintenance", className: "text-blue-600" },
    decommissioned: { label: "page.robots.lifecycle.decommissioned", className: "text-zinc-600" },
};

const chargingConfig: Record<ChargingState, { label: string; className: string }> = {
    charging: { label: "page.robots.charging.charging", className: "text-blue-600" },
    discharging: { label: "page.robots.charging.discharging", className: "text-muted-foreground" },
    full: { label: "page.robots.charging.full", className: "text-green-600" },
};

const healthConfig: Record<RobotHealth, { label: string; dotClassName: string }> = {
    healthy: { label: "page.robots.health.healthy", dotClassName: "bg-green-500" },
    warning: { label: "page.robots.health.warning", dotClassName: "bg-amber-500" },
    critical: { label: "page.robots.health.critical", dotClassName: "bg-red-500" },
    unknown: {
        label: "page.robots.health.unknown",
        dotClassName: "bg-muted-foreground",
    },
};

const availabilityConfig: Record<
    RobotAvailability,
    { label: string; variant: BadgeProps["variant"] }
> = {
    available: { label: "page.robots.availability.available", variant: "lightGreen" },
    blocked: { label: "page.robots.availability.blocked", variant: "lightRed" },
    maintenance: { label: "page.robots.availability.maintenance", variant: "lightOrange" },
    charging: {
        label: "page.robots.availability.charging",
        variant: "lightBlue",
    },
    busy: {
        label: "page.robots.availability.busy",
        variant: "lightYellow",
    },
    paused: {
        label: "page.robots.availability.paused",
        variant: "lightOrange",
    },
};

const SortableHead = ({
    label,
    column,
    sortColumn,
    sortDirection,
    onSort,
    className,
}: {
    label: string;
    column: RobotSortKey;
    sortColumn: RobotSortKey | null;
    sortDirection: SortDirection;
    onSort: (column: RobotSortKey) => void;
    className?: string;
}): JSX.Element => {
    const isActive = sortColumn === column;
    const SortIcon = !isActive
        ? ArrowUpDown
        : sortDirection === SortDirection.ASCENDING
          ? ArrowUp
          : ArrowDown;

    return (
        <TableHead className={className}>
            <button
                onClick={() => onSort(column)}
                className={cn(
                    "hover:text-foreground inline-flex cursor-pointer items-center gap-1.5",
                    isActive && "text-foreground",
                )}
            >
                {label}
                <SortIcon className="size-3.5" />
            </button>
        </TableHead>
    );
};

const DetailField = ({
    label,
    children,
}: React.PropsWithChildren<{ label: string }>): JSX.Element => (
    <div>
        <div className="text-muted-foreground text-sm">{label}</div>
        <div className="mt-1 flex items-center gap-1.5 text-sm">{children}</div>
    </div>
);

const RobotRow = observer(
    ({
        robot,
        onEdit,
        onDelete,
    }: {
        robot: Robot;
        onEdit: () => void;
        onDelete: () => void;
    }): JSX.Element => {
        const { t } = useTranslation();
        const [expanded, setExpanded] = useState(false);

        const department = robot.departmentId
            ? store.repositoryDepartments.byId(robot.departmentId)
            : undefined;

        const connectivity = connectivityConfig[robot.connectivity];
        const charging = robot.charging ? chargingConfig[robot.charging] : undefined;
        const health = healthConfig[robot.health];
        const availability = robot.availability
            ? availabilityConfig[robot.availability]
            : undefined;
        const lifecycle = lifecycleConfig[robot.lifecycle];

        // Using inset box shadow instead of border to avoid gaps in not expanded rows.
        const expandedClassName =
            expanded && "shadow-[inset_4px_0_0_0_var(--color-yellow-400)] bg-zinc-50/50";

        return (
            <>
                <TableRow
                    className={cn(expandedClassName, expanded && "border-b-0")}
                    onClick={() => setExpanded(!expanded)}
                >
                    <TableCell>
                        <div className="flex items-center gap-3">
                            <DecoratedIcon
                                icon={Bot}
                                variant={robot.kind === "amr" ? "blue" : "default"}
                            />
                            <span className="font-medium">{robot.name}</span>
                        </div>
                    </TableCell>
                    <TableCell>
                        <div className="flex items-center gap-2">
                            <span
                                className={cn("size-2 rounded-full", connectivity.dotClassName)}
                            />
                            {t(connectivity.label)}
                        </div>
                    </TableCell>
                    <TableCell>
                        <div className="flex items-center gap-3">
                            <Badge variant={robot.kind === "amr" ? "lightBlue" : "secondary"}>
                                {robot.kind.toUpperCase()}
                            </Badge>
                            {t(typeLabels[robot.type])}
                        </div>
                    </TableCell>
                    <TableCell className="text-right">
                        {robot.battery != null ? `${robot.battery}%` : "—"}
                    </TableCell>
                    <TableCell>
                        {charging ? (
                            <div className={cn("flex items-center gap-1.5", charging.className)}>
                                {robot.charging === "charging" && <Zap className="size-4" />}
                                {t(charging.label)}
                            </div>
                        ) : (
                            <span className="text-muted-foreground">—</span>
                        )}
                    </TableCell>
                    <TableCell className="text-center">
                        {robot.alarms > 0 ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-600">
                                <TriangleAlert className="size-3.5" />
                                {robot.alarms}
                            </span>
                        ) : (
                            <span className="text-muted-foreground">—</span>
                        )}
                    </TableCell>
                    <TableCell className="w-10 p-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                                e.stopPropagation();
                                setExpanded(!expanded);
                            }}
                        >
                            <ChevronRight
                                className={cn("transition-transform", expanded && "-rotate-90")}
                            />
                        </Button>
                    </TableCell>
                    <TableCell className="w-10 p-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                    <EllipsisVertical />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem className="cursor-pointer" onClick={onEdit}>
                                    {t("page.robots.edit")}
                                </DropdownMenuItem>
                                <DropdownMenuItem className="cursor-pointer" onClick={onDelete}>
                                    {t("page.robots.delete")}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </TableCell>
                </TableRow>
                {expanded && (
                    <TableRow className={cn(expandedClassName, "hover:bg-zinc-50/50")}>
                        <TableCell colSpan={8}>
                            <div className="flex flex-col gap-4 pb-2">
                                <div className="flex items-center gap-2 font-semibold">
                                    <DecoratedIcon icon={FileIcon} />
                                    {t("page.robots.details.specification")}
                                </div>
                                <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
                                    <DetailField label={t("page.robots.details.type")}>
                                        {t(typeLabels[robot.type])}
                                    </DetailField>
                                    <DetailField label={t("page.robots.details.model")}>
                                        {robot.model}
                                    </DetailField>
                                    <DetailField label={t("page.robots.details.manufacturer")}>
                                        {robot.manufacturer}
                                    </DetailField>
                                    <DetailField label={t("page.robots.details.department")}>
                                        {department ? store.i18n.formatDepartment(department) : "—"}
                                    </DetailField>
                                </div>
                                <div className="border-b" />
                                <div className="flex items-center gap-2 font-semibold">
                                    <DecoratedIcon icon={CircleCheckBig} />
                                    {t("page.robots.details.status")}
                                </div>
                                <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
                                    <DetailField label={t("page.robots.details.lifecycleState")}>
                                        {lifecycle ? t(lifecycle.label) : "—"}
                                    </DetailField>
                                    <DetailField label={t("page.robots.details.health")}>
                                        <span
                                            className={cn(
                                                "size-2 rounded-full",
                                                health.dotClassName,
                                            )}
                                        />
                                        {t(health.label)}
                                    </DetailField>
                                    <DetailField label={t("page.robots.details.availability")}>
                                        {availability ? (
                                            <Badge
                                                variant={availability.variant}
                                                className="rounded-full"
                                            >
                                                {t(availability.label)}
                                            </Badge>
                                        ) : (
                                            "—"
                                        )}
                                    </DetailField>
                                    <DetailField label={t("page.robots.details.lastSeen")}>
                                        {robot.lastSeen
                                            ? store.i18n.formatDateTime(robot.lastSeen)
                                            : "—"}
                                    </DetailField>
                                </div>
                            </div>
                        </TableCell>
                    </TableRow>
                )}
            </>
        );
    },
);

export type RobotListProps = {
    robots: Robot[];
    sortColumn: RobotSortKey | null;
    sortDirection: SortDirection;
    onSort: (column: RobotSortKey) => void;
    onEdit: (robot: Robot) => void;
    onDelete: (robot: Robot) => void;
};

export const RobotList = observer(
    ({
        robots,
        sortColumn,
        sortDirection,
        onSort,
        onEdit,
        onDelete,
    }: RobotListProps): JSX.Element => {
        const { t } = useTranslation();
        const [robotToDelete, setRobotToDelete] = useState<Robot | null>(null);

        return (
            <>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <SortableHead
                                label={t("page.robots.column.name")}
                                column="name"
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={onSort}
                            />
                            <SortableHead
                                label={t("page.robots.column.connectivity")}
                                column="connectivity"
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={onSort}
                            />
                            <TableHead>{t("page.robots.column.type")}</TableHead>
                            <SortableHead
                                label={t("page.robots.column.battery")}
                                column="battery"
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={onSort}
                                className="text-right"
                            />
                            <TableHead>{t("page.robots.column.charging")}</TableHead>
                            <SortableHead
                                label={t("page.robots.column.alarms")}
                                column="alarms"
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={onSort}
                                className="text-center"
                            />
                            <TableHead className="w-10" />
                            <TableHead className="w-10" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {robots.map((robot) => (
                            <RobotRow
                                robot={robot}
                                onEdit={() => onEdit(robot)}
                                onDelete={() => setRobotToDelete(robot)}
                                key={robot.id}
                            />
                        ))}
                    </TableBody>
                </Table>
                <ConfirmationDialog
                    open={robotToDelete != null}
                    onOpenChange={(open) => {
                        if (!open) {
                            setRobotToDelete(null);
                        }
                    }}
                    title={t("page.robots.deleteDialog.title")}
                    description={t("page.robots.deleteDialog.description", {
                        name: robotToDelete?.name,
                    })}
                    destructive
                    onConfirm={() => {
                        if (robotToDelete) {
                            store.serviceRobots.removeRobot(robotToDelete.id);
                            toast.success(
                                t("page.robots.toast.deleted", { name: robotToDelete.name }),
                            );
                            onDelete(robotToDelete);
                        }
                    }}
                />
            </>
        );
    },
);
